import {
  AdviceProductDetailsFormValues,
  DebtResponsible,
  FamilySituation,
} from "Features/CheckPrice/Products/Advice/ProductDetails/FormElements/interfaces";
import { InsuranceCoverage, PaymentReason } from "Store/advice/interfaces";
import {
  CoverageNeed,
  ProductCoverageName,
} from "Store/akaAdvice/interfaces/CollectiveAdvice";

import { Description } from "Features/CheckPrice/Products/Advice/adviceStyled";
import { isDisplayedAndNotChecked } from "Store/akaAdvice/interfaces/getters";
import { isDefined } from "Utils/functions";
import React from "react";

interface Props {
  need: CoverageNeed;
  adviceDetails: AdviceProductDetailsFormValues;
  adviceTexts: any;
  lang: string;
}

export const AkaDeathAdviceText = ({
  need,
  adviceDetails,
  adviceTexts,
  lang,
}: // eslint-disable-next-line sonarjs/cognitive-complexity
Props): React.ReactElement => {
  const recommendedCoverage = need.recommendedCoverage;
  const paymentReasons = need.needReasons;
  const displayedAndNotChecked =
    isDisplayedAndNotChecked(
      need,
      ProductCoverageName.AKA_GROUP_LIFE_INSURED_DEATH,
    ) && isDisplayedAndNotChecked(need, ProductCoverageName.DEATH);

  if (displayedAndNotChecked) {
    return (
      <Description fw={400} size="14px" className="AdviceTexts">
        Basert på dine svar anbefaler vi i utgangspunktet ikke livsforsikring,
        men du kan likevel velge livsforsikring hvis du ønsker forsikringen.
      </Description>
    );
  }

  const { desc } = adviceTexts;
  const weAdvice = "Dette tilsvarer ";

  let forYourSituation = "";
  const debt = isDefined(paymentReasons[PaymentReason.DEBT_DEPENDENTS]);
  const children = isDefined(paymentReasons[PaymentReason.MINOR_CHILDREN]);
  const familySituation = adviceDetails.familySituation;
  const debtResponsible = adviceDetails.debtResponsible;
  const notAlone =
    familySituation !== FamilySituation.SINGLE &&
    debtResponsible &&
    debtResponsible !== DebtResponsible.ME_ALONE;
  if (debt && children) {
    if (notAlone) {
      forYourSituation = desc.part2.youBoth.debtAndChildren[lang];
    } else {
      forYourSituation = desc.part2.youAlone.debtAndChildren[lang];
    }
  } else if (debt) {
    if (notAlone) {
      forYourSituation = desc.part2.youBoth.debt[lang];
      if (
        debtResponsible === DebtResponsible.BOTH &&
        recommendedCoverage === InsuranceCoverage.BASIC
      ) {
        forYourSituation = `${desc.part2.youBoth.debt.halfDebt[lang]}${desc.part2.youBoth.debt[lang]}`;
      }
    } else {
      forYourSituation = desc.part2.youAlone.debt[lang];
    }
  } else if (children) {
    if (notAlone) {
      forYourSituation = desc.part2.youBoth.children[lang];
    } else {
      forYourSituation = desc.part2.youAlone.children[lang];
    }
  } else {
    forYourSituation = desc.part2.default[lang];
    if (debtResponsible === DebtResponsible.SPOUSE_COHABITANT_ALONE) {
      forYourSituation = `${desc.part2.default[lang]}${desc.part2.default.spouseCohabitantAlone[lang]}`;
    }
  }

  let inAdditionItCovers = "";
  if (isDefined(paymentReasons[PaymentReason.INCOME_DEPENDENTS])) {
    const aValueBeing = desc.part4.subPart1[lang];
    let amount = "";
    if (recommendedCoverage === InsuranceCoverage.TOP) {
      amount = desc.part4.subPart2.top[lang];
    } else if (
      recommendedCoverage === InsuranceCoverage.PLUS ||
      recommendedCoverage === InsuranceCoverage.BASIC
    ) {
      amount = desc.part4.subPart2.notTop[lang];
    }
    const ofYearlyIncome = "årslønn siden du har svart at din ";
    let sinceYourPartner = "";
    if (familySituation === FamilySituation.COHABITANT) {
      sinceYourPartner = desc.part4.subPart4.cohabitant[lang];
    } else if (familySituation === FamilySituation.MARRIED) {
      sinceYourPartner = desc.part4.subPart4.married[lang];
    }
    const isIncomeDependent = desc.part4.subPart5[lang];
    inAdditionItCovers = `${aValueBeing}${amount}${ofYearlyIncome}${sinceYourPartner}${isIncomeDependent}`;
  }

  const helpSurvivors =
    ". Utbetalingene kan hjelpe de etterlatte med å beholde boligen og klare seg uten din inntekt.";

  return (
    <Description fw={400} size="14px" className="AdviceTextsReason">
      {`${weAdvice}${forYourSituation}${inAdditionItCovers}${helpSurvivors}`}
    </Description>
  );
};
